import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Button,
  Spinner,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";
import { isEqual } from "lodash";
import moment from "moment";
import "moment/locale/es";
import { auth } from "../../firebase";

const TCSOCKET_URL =
  process.env.REACT_APP_TCSOCKET_URL || "https://api.ticketcontrol.net";

const DRAFT_KEY = (machineKey) => `oper_permissions_draft_${machineKey}`;

// Claves de permisos que viven en `app_config.ticket.*` en tc_qml,
// no en `permissions.oper`. Se envían al backend en el bloque `ticket`
// del payload y se mergean en esa sección del app_config.
const TICKET_STORE_KEYS = new Set([
  "cancel_time_enabled",
  "cancel_time",
  "edit_time_enabled",
  "edit_time",
  "discount",
]);

// ─────────────────────────────────────────────────────────
// Metadata de secciones y permisos.
// Cada permiso describe:
//   key: path dentro de permissions.oper (ej. "cash.close_x")
//   label: texto visible
//   help: descripción corta
//   requires: condición para mostrar (función que recibe helpers)
// ─────────────────────────────────────────────────────────
// Orden y contenido espejan la UI local de tc_qml. Mantener sincronizado
// con Pages/Main/.../PermissionsConfig al cambiar algo.
const SECTIONS = [
  {
    key: "_root",
    title: "Tickets",
    icon: "confirmation_number",
    items: [
      {
        key: "cancel_time_enabled",
        type: "segmented",
        label: "Cancelación de tickets",
        help: "Permite al operador cancelar tickets abiertos",
        options: ["Deshabilitada", "Habilitada", "Por tiempo"],
        subField: {
          when: 2, // se muestra cuando la opción es "Por tiempo"
          key: "cancel_time",
          label: "Tiempo máximo para cancelar",
          suffix: "min",
        },
      },
      {
        key: "edit_time_enabled",
        type: "segmented",
        label: "Edición de tickets",
        help: "Permite al operador editar datos de tickets abiertos",
        options: ["Deshabilitada", "Habilitada", "Por tiempo"],
        subField: {
          when: 2,
          key: "edit_time",
          label: "Tiempo máximo para editar",
          suffix: "min",
        },
      },
      {
        key: "discount",
        type: "segmented",
        label: "Descuentos",
        help: "Permite aplicar descuentos al cobrar",
        options: ["No", "Solo Admin", "Todos"],
      },
      {
        key: "history_closed_tickets",
        label: "Ver historial de tickets cerrados",
        help: "Permite al operador consultar tickets ya cerrados",
      },
    ],
  },
  {
    key: "cash",
    title: "Caja",
    icon: "point_of_sale",
    items: [
      {
        key: "invoices_create",
        label: "Crear Facturas Manuales",
        help: "Permite crear facturas electrónicas ARCA desde la caja",
        requires: "invoices",
      },
      {
        key: "invoices_nc_create",
        label: "Crear Notas de Crédito (ARCA)",
        help: "Permite anular facturas emitidas por ARCA",
        requires: "invoices",
      },
      {
        key: "close_x",
        label: "Realizar Cierres 'X' (Facturas)",
        help: "Cierre parcial: genera reporte sin cerrar la caja",
        requires: "invoices",
      },
      {
        key: "close_z",
        label: "Realizar Cierres 'Z' (Facturas)",
        help: "Cierre definitivo: cierra la caja y genera reporte final",
        requires: "invoices",
      },
      {
        key: "cash_withdrawals",
        label: "Permitir Movimientos Negativos (Retiros)",
        help: "Permite registrar retiros de efectivo de la caja",
      },
      {
        key: "vouchers_mandatory",
        label: "Comprobante Obligatorio",
        help: "Obliga a emitir Recibo o Factura Electrónica al cobrar",
      },
      {
        key: "cash_resume",
        label: "Ver Resumen de Caja",
        help: "Permite consultar los totales y movimientos del turno",
      },
    ],
  },
  {
    key: "cc",
    title: "Abonados (Cuentas Corrientes)",
    icon: "groups",
    requires: "cc",
    groups: [
      {
        subtitle: "Cuentas",
        items: [
          { key: "add", label: "Agregar o Reactivar Cuentas", help: "Permite crear nuevos abonados o reactivar cuentas vencidas" },
          { key: "edit", label: "Editar Cuentas", help: "Permite modificar datos de cuentas existentes" },
          { key: "delete", label: "Eliminar Cuentas", help: "Permite eliminar cuentas de abonados" },
          { key: "end", label: "Finalizar Cuentas", help: "Permite dar de baja cuentas activas" },
          { key: "add_charge", label: "Crear Cargos", help: "Permite registrar cobros en las cuentas" },
          { key: "adjust", label: "Ajustar Cuenta Corriente", help: "Permite modificar el saldo manualmente" },
        ],
      },
      {
        subtitle: "Vehículos",
        items: [
          { key: "vehicles_add", label: "Agregar Vehículos", help: "Permite asociar vehículos a una cuenta" },
          { key: "vehicles_edit", label: "Editar Vehículos", help: "Permite modificar datos de vehículos asociados" },
          { key: "vehicles_delete", label: "Eliminar Vehículos", help: "Permite desvincular vehículos de una cuenta" },
        ],
      },
    ],
  },
  {
    key: "deals",
    title: "Acuerdos (Empresas)",
    icon: "handshake",
    requires: "deals",
    items: [
      { key: "view", label: "Ver Acuerdos", help: "Permite ver la lista de empresas y sus cuentas corrientes" },
      { key: "pay", label: "Registrar Pagos", help: "Permite registrar pagos en la cuenta corriente de empresas" },
      { key: "charge", label: "Crear Cargos", help: "Permite registrar cargos (deudas) a empresas" },
      { key: "adjust", label: "Ajustar Saldo", help: "Permite modificar el saldo manualmente" },
      { key: "export", label: "Exportar y Enviar por Email", help: "Permite exportar movimientos a Excel y enviar por email" },
    ],
  },
];

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────
function getPerm(oper, section, key) {
  if (section === "_root") return oper?.[key];
  return oper?.[section]?.[key];
}

function setPerm(oper, section, key, value) {
  const next = { ...oper };
  if (section === "_root") {
    next[key] = value;
  } else {
    next[section] = { ...(oper[section] || {}), [key]: value };
  }
  return next;
}

// Detecta qué módulos opcionales tiene habilitados este cliente.
// Usa el mismo criterio que tc_qml local (`isModuleEnabled`) para que la
// UI de Monitor se mantenga sincronizada con la de la máquina.
function detectFeatures(appConfig, tariffs) {
  const modules = appConfig?.modules || {};
  // Facturación electrónica: módulo invoices activo.
  const hasInvoices = modules.invoices === 1;
  // Abonados (Cta.Cte): si hay algún timelapse type=cc activo en tariffs.
  const hasCc = !!(
    tariffs &&
    Array.isArray(tariffs.timelapses) &&
    tariffs.timelapses.some((t) => t.type === "cc")
  );
  // Acuerdos (empresas con CC).
  const hasDeals = modules.deals === 1;
  return { hasInvoices, hasCc, hasDeals };
}

function sectionVisible(section, features) {
  if (section.requires === "cc") return features.hasCc;
  if (section.requires === "deals") return features.hasDeals;
  return true;
}

function itemVisible(item, features) {
  if (item.requires === "invoices") return features.hasInvoices;
  if (item.requires === "cc") return features.hasCc;
  if (item.requires === "deals") return features.hasDeals;
  return true;
}

// ─────────────────────────────────────────────────────────
// Segmented control (botones toggle de N opciones)
// Útil para permisos con 3 estados (ej. Cancelación: Deshabilitada /
// Habilitada / Por tiempo). El valor guardado es el índice (0..N-1).
// ─────────────────────────────────────────────────────────
function SegmentedControl({ value, options, disabled, onChange }) {
  return (
    <div className="d-inline-flex" style={{ gap: 4 }}>
      {options.map((opt, idx) => {
        const active = value === idx;
        return (
          <Button
            key={idx}
            color={active ? "primary" : "light"}
            outline={!active}
            size="sm"
            disabled={disabled}
            onClick={() => onChange(idx)}
            style={{ fontSize: 12, padding: "4px 14px" }}
          >
            {opt}
          </Button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Switch inline
// ─────────────────────────────────────────────────────────
function PermissionSwitch({ checked, disabled, onChange }) {
  return (
    <label
      className="d-inline-flex align-items-center"
      style={{ cursor: disabled ? "default" : "pointer", margin: 0 }}
    >
      <Input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked ? 1 : 0)}
        style={{ marginTop: 0, width: 18, height: 18 }}
      />
    </label>
  );
}

function PermissionRow({
  item,
  value,
  canEdit,
  onChange,
  // Props opcionales para el subcampo numérico condicional (ej.
  // "Tiempo máximo" cuando cancel_time_enabled === 2).
  subFieldValue,
  onSubFieldChange,
}) {
  const showSubField =
    item.type === "segmented" && item.subField && value === item.subField.when;

  return (
    <>
      <div
        className="d-flex align-items-center py-2"
        style={{
          borderBottom: showSubField ? "none" : "1px solid #eef2f7",
        }}
      >
        <div style={{ flex: 1, paddingRight: 16 }}>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{item.label}</div>
          {item.help && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              {item.help}
            </div>
          )}
        </div>
        {item.type === "segmented" ? (
          <SegmentedControl
            value={parseInt(value ?? 0, 10) || 0}
            options={item.options}
            disabled={!canEdit}
            onChange={(v) => onChange(v)}
          />
        ) : (
          <PermissionSwitch
            checked={value}
            disabled={!canEdit}
            onChange={(v) => onChange(v)}
          />
        )}
      </div>

      {showSubField && (
        <div
          className="d-flex align-items-center py-2"
          style={{
            borderBottom: "1px solid #eef2f7",
            paddingLeft: 24,
            fontSize: 13,
          }}
        >
          <div style={{ flex: 1, color: "#495057" }}>
            {item.subField.label}
          </div>
          <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <Input
              type="number"
              bsSize="sm"
              min={0}
              value={subFieldValue ?? 0}
              disabled={!canEdit}
              onChange={(e) =>
                onSubFieldChange(parseInt(e.target.value, 10) || 0)
              }
              style={{ width: 70 }}
            />
            <span className="text-muted small">{item.subField.suffix || "min"}</span>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────
const PermisosOperadores = ({
  currentCompany,
  appConfig,
  tariffs,
  canEditPermissions,
}) => {
  document.title = "Permisos de Operadores | Monitor";
  const machineKey = currentCompany?.id;

  // Combinamos `permissions.oper` + las claves de tickets que viven en
  // `app_config.ticket.*`, así el UI ve todo junto como si fuera un
  // solo objeto de permisos.
  const operFromConfig = useMemo(() => {
    if (!appConfig) return null;
    const oper = appConfig.permissions?.oper;
    const ticket = appConfig.ticket;
    if (!oper && !ticket) return null;
    const merged = { ...(oper || {}) };
    if (ticket) {
      TICKET_STORE_KEYS.forEach((k) => {
        if (ticket[k] !== undefined) merged[k] = ticket[k];
      });
    }
    return merged;
  }, [appConfig]);

  const [draft, setDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);
  const lastSyncedRef = useRef(null);

  const features = useMemo(
    () => detectFeatures(appConfig, tariffs),
    [appConfig, tariffs]
  );

  // Sincronizar draft con app_config.permissions.oper
  useEffect(() => {
    if (!operFromConfig) {
      setDraft(null);
      lastSyncedRef.current = null;
      return;
    }
    if (!canEditPermissions) {
      setDraft(null);
      lastSyncedRef.current = operFromConfig;
      return;
    }
    // Cargar draft de localStorage si es coherente con el snapshot
    if (!draft && machineKey) {
      try {
        const saved = localStorage.getItem(DRAFT_KEY(machineKey));
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && isEqual(parsed.base, operFromConfig)) {
            setDraft(parsed.draft);
            lastSyncedRef.current = operFromConfig;
            return;
          }
          localStorage.removeItem(DRAFT_KEY(machineKey));
        }
      } catch (_) {}
      setDraft(JSON.parse(JSON.stringify(operFromConfig)));
      lastSyncedRef.current = operFromConfig;
      return;
    }
    // Si el snapshot cambió remotamente
    if (draft && !isEqual(operFromConfig, lastSyncedRef.current)) {
      const isDirty = !isEqual(draft, lastSyncedRef.current);
      // Si llegó justo lo que hay en pantalla, es el propio cambio recién
      // enviado, ya aplicado por la máquina: no hay nada sin guardar (antes
      // avisaba «Tenés cambios locales sin guardar» junto al de éxito).
      const llegoLoQueHayEnPantalla = isEqual(draft, operFromConfig);
      if (!isDirty || llegoLoQueHayEnPantalla) {
        setDraft(JSON.parse(JSON.stringify(operFromConfig)));
      } else {
        toast.info(
          "La máquina actualizó los permisos. Tenés cambios locales sin guardar.",
          { autoClose: 6000 }
        );
      }
      lastSyncedRef.current = operFromConfig;
    }
  }, [operFromConfig, canEditPermissions, machineKey, draft]);

  // Persistir draft en localStorage
  useEffect(() => {
    if (!machineKey || !canEditPermissions || !draft || !operFromConfig) return;
    if (isEqual(draft, operFromConfig)) {
      localStorage.removeItem(DRAFT_KEY(machineKey));
    } else {
      try {
        localStorage.setItem(
          DRAFT_KEY(machineKey),
          JSON.stringify({ draft, base: operFromConfig })
        );
      } catch (_) {}
    }
  }, [draft, operFromConfig, canEditPermissions, machineKey]);

  const isDirty = useMemo(() => {
    if (!draft || !operFromConfig) return false;
    return !isEqual(draft, operFromConfig);
  }, [draft, operFromConfig]);

  const effective = canEditPermissions && draft ? draft : operFromConfig;

  const handleChange = (section, key, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return setPerm(prev, section, key, value);
    });
    setServerErrors(null);
  };

  const handleDiscard = () => {
    if (!operFromConfig) return;
    setDraft(JSON.parse(JSON.stringify(operFromConfig)));
    setServerErrors(null);
    if (machineKey) localStorage.removeItem(DRAFT_KEY(machineKey));
    toast.info("Cambios descartados");
  };

  const handleSend = async () => {
    if (!machineKey || !draft || sending) return;
    setSending(true);
    setServerErrors(null);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Sesión expirada. Volvé a iniciar sesión.");
        setSending(false);
        return;
      }
      const token = await user.getIdToken();
      // Separar el draft: las claves de TICKET_STORE_KEYS van a
      // `payload.ticket` (app_config.ticket.*), el resto va a
      // `payload.oper` (app_config.permissions.oper).
      const operPart = {};
      const ticketPart = {};
      for (const [k, v] of Object.entries(draft)) {
        if (TICKET_STORE_KEYS.has(k)) ticketPart[k] = v;
        else operPart[k] = v;
      }
      const res = await fetch(`${TCSOCKET_URL}/monitor/permissions-update`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: machineKey,
          payload: { oper: operPart, ticket: ticketPart },
        }),
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (_) {
        data = { message: text };
      }
      console.log("[permisos] HTTP", res.status, data);
      if (!res.ok) {
        const code = data.error || `HTTP ${res.status}`;
        console.warn(`[permisos] error code=${code}`, data);
        toast.error(data.message || `Error al aplicar permisos [${code}]`, {
          autoClose: 8000,
        });
        if (Array.isArray(data.errors)) setServerErrors(data.errors);
      } else {
        toast.success("Permisos aplicados en la máquina");
        localStorage.removeItem(DRAFT_KEY(machineKey));
      }
    } catch (err) {
      toast.error(`Error de red: ${err.message || err}`, { autoClose: 8000 });
    } finally {
      setSending(false);
    }
  };

  // ── Render ──
  if (!machineKey) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                group
              </span>
              <h5 className="text-muted mb-0">Selecciona un cliente primero</h5>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  if (!effective) {
    return (
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody className="text-center py-5">
              <span
                className="material-symbols-rounded d-block mb-2"
                style={{ fontSize: 48, color: "#ccc" }}
              >
                sync_disabled
              </span>
              <h5 className="text-muted mb-0">
                Este cliente aún no sincronizó su configuración
              </h5>
              <p className="text-muted small mt-2">
                Los permisos se leen desde <code>app_config.permissions.oper</code>
                {" "}que se sincroniza desde tc_qml.
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">Permisos de Operadores</h4>
            <p className="text-muted small mb-0">
              {canEditPermissions
                ? "Definí qué acciones puede realizar cada operador en la máquina de tu estacionamiento."
                : "Qué acciones puede realizar cada operador. Solo lectura."}
            </p>
          </Col>
          <Col xs="auto">
            {appConfig?.updatedAt && (
              <div className="text-end small text-muted">
                <span
                  className="material-symbols-rounded align-middle me-1"
                  style={{ fontSize: 16 }}
                >
                  update
                </span>
                Última sincronización:{" "}
                <b>{moment(appConfig.updatedAt).locale("es").fromNow()}</b>
                <div style={{ fontSize: 11 }}>
                  {moment(appConfig.updatedAt).format("DD/MM/YYYY HH:mm")}
                </div>
              </div>
            )}
          </Col>
        </Row>

        {/* Banner experimental */}
        <Card
          className="mb-3"
          style={{
            borderLeft: "4px solid #50a5f1",
            backgroundColor: "rgba(80, 165, 241, 0.06)",
          }}
        >
          <CardBody className="py-2 px-3">
            <div className="d-flex align-items-start">
              <span
                className="material-symbols-rounded me-2"
                style={{ fontSize: 22, color: "#50a5f1", marginTop: 2 }}
              >
                science
              </span>
              <div style={{ flex: 1, fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: "#2b6cb0" }}>
                  Funcionalidad en etapa de pruebas
                </div>
                <div className="text-muted" style={{ marginTop: 2 }}>
                  Ante cualquier comportamiento inesperado, avisanos a soporte
                  técnico. Siempre podés ajustar permisos desde TicketControl
                  en el estacionamiento.
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {SECTIONS.filter((s) => sectionVisible(s, features)).map((section) => {
          const visibleItems = section.items
            ? section.items.filter((it) => itemVisible(it, features))
            : [];
          const visibleGroups = section.groups
            ? section.groups
                .map((g) => ({
                  ...g,
                  items: g.items.filter((it) => itemVisible(it, features)),
                }))
                .filter((g) => g.items.length > 0)
            : [];

          if (
            (!section.items || visibleItems.length === 0) &&
            (!section.groups || visibleGroups.length === 0)
          ) {
            return null;
          }

          return (
            <Card key={section.key} className="mb-3 shadow-sm">
              <CardBody>
                <div className="d-flex align-items-center mb-3">
                  <span
                    className="material-symbols-rounded me-2"
                    style={{ fontSize: 22, color: "#556ee6" }}
                  >
                    {section.icon}
                  </span>
                  <h5 className="mb-0">{section.title}</h5>
                </div>

                {/* Items directos */}
                {section.items &&
                  visibleItems.map((item) => (
                    <PermissionRow
                      key={item.key}
                      item={item}
                      value={getPerm(effective, section.key, item.key)}
                      canEdit={canEditPermissions}
                      onChange={(v) => handleChange(section.key, item.key, v)}
                      subFieldValue={
                        item.subField
                          ? getPerm(effective, section.key, item.subField.key)
                          : undefined
                      }
                      onSubFieldChange={
                        item.subField
                          ? (v) =>
                              handleChange(section.key, item.subField.key, v)
                          : undefined
                      }
                    />
                  ))}

                {/* Grupos con subtítulo */}
                {section.groups &&
                  visibleGroups.map((group, gi) => (
                    <div key={gi} className={gi > 0 ? "mt-3" : ""}>
                      <h6
                        className="text-muted"
                        style={{
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 8,
                        }}
                      >
                        {group.subtitle}
                      </h6>
                      {group.items.map((item) => (
                        <PermissionRow
                          key={item.key}
                          item={item}
                          value={getPerm(effective, section.key, item.key)}
                          canEdit={canEditPermissions}
                          onChange={(v) =>
                            handleChange(section.key, item.key, v)
                          }
                        />
                      ))}
                    </div>
                  ))}
              </CardBody>
            </Card>
          );
        })}

        {/* Errores del server */}
        {canEditPermissions &&
          serverErrors &&
          serverErrors.length > 0 && (
            <Card
              className="mb-4"
              style={{
                borderLeft: "4px solid #f46a6a",
                backgroundColor: "rgba(244, 106, 106, 0.06)",
                marginBottom: 90,
              }}
            >
              <CardBody>
                <div style={{ fontWeight: 600, color: "#b84040" }}>
                  Problemas que impiden guardar
                </div>
                <ul className="small mb-0 mt-2" style={{ paddingLeft: 18 }}>
                  {serverErrors.map((e, i) => (
                    <li key={i}>
                      {e.field ? <b>{e.field}: </b> : null}
                      {e.message}
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          )}

        {/* Spacer para barra flotante */}
        {canEditPermissions && isDirty && <div style={{ height: 80 }} />}

        {/* Barra de acción flotante */}
        {canEditPermissions && isDirty && (
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "#2a3042",
              color: "#fff",
              borderTop: "1px solid #1b1e2c",
              boxShadow: "0 -4px 16px rgba(0,0,0,0.25)",
              padding: "14px 24px",
              zIndex: 1030,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 24, color: "#ffd76b" }}
            >
              edit_note
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: "#fff", fontSize: 14 }}>
                Tenés cambios sin guardar
              </div>
              <div style={{ fontSize: 12, color: "#a8b0c3" }}>
                Los permisos se aplicarán en la máquina del estacionamiento de
                forma inmediata al enviar. Asegurate de que tenga conexión
                estable a internet.
              </div>
            </div>
            <Button
              color="link"
              onClick={handleDiscard}
              disabled={sending}
              style={{ textDecoration: "none", color: "#ced4e0", fontWeight: 500 }}
            >
              Descartar
            </Button>
            <Button
              color="success"
              onClick={handleSend}
              disabled={sending}
              style={{ fontWeight: 600, padding: "8px 20px" }}
            >
              {sending ? (
                <>
                  <Spinner size="sm" className="me-1" /> Enviando...
                </>
              ) : (
                <>
                  <span
                    className="material-symbols-rounded align-middle me-1"
                    style={{ fontSize: 16 }}
                  >
                    send
                  </span>
                  Enviar cambios
                </>
              )}
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentCompany: state.companies.currentCompany,
  appConfig: state.data.appConfig,
  tariffs: state.data.tariffs,
  // Por ahora reutilizamos canEditTariffs (el flag general de permiso).
  // A futuro puede diferenciarse con can_edit_permissions.
  canEditPermissions: state.data.canEditTariffs,
});

export default connect(mapStateToProps)(PermisosOperadores);
